import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * Photo gallery modal: shows one photo at a time with prev/next controls and
 * a small thumbnail strip. Closable via close button or clicking outside.
 */
export default function PhotoGalleryModal({ open, onOpenChange, title, photos = [] }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => { if (open) setIdx(0); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, photos.length, onOpenChange]);

  if (!photos || photos.length === 0) return null;

  const clampedIdx = Math.min(idx, photos.length - 1);
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="photo-gallery-modal"
        className="max-h-[92vh] max-w-3xl overflow-y-auto p-0 sm:max-w-3xl"
      >
        <DialogHeader className="border-b border-border p-4">
          <DialogTitle className="font-heading text-xl">{title || "Foto Vendor"}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Foto {clampedIdx + 1} dari {photos.length}
          </DialogDescription>
        </DialogHeader>

        <div className="relative flex items-center justify-center bg-black/95">
          <img
            src={photos[clampedIdx]}
            alt={`Foto ${clampedIdx + 1}`}
            data-testid={`gallery-photo-${clampedIdx}`}
            className="max-h-[65vh] w-auto object-contain"
          />
          {photos.length > 1 && (
            <>
              <Button
                data-testid="gallery-prev"
                onClick={prev}
                size="icon"
                variant="ghost"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white"
                aria-label="Sebelumnya"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                data-testid="gallery-next"
                onClick={next}
                size="icon"
                variant="ghost"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 hover:text-white"
                aria-label="Selanjutnya"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </div>

        {photos.length > 1 && (
          <div className="flex flex-wrap gap-2 border-t border-border p-3">
            {photos.map((src, i) => (
              <button
                key={i}
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setIdx(i)}
                className={`h-14 w-14 overflow-hidden rounded-md border-2 transition-colors ${
                  i === clampedIdx ? "border-primary" : "border-transparent hover:border-border"
                }`}
              >
                <img src={src} alt={`Thumb ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
